// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { Sort } from '@/constants/sort';
import { costListingSchema } from '@/costs/api/types';
import { z } from 'zod';

/**
 * Schema defining the valid properties for sorting costs.
 */
export const costSortPropertySchema = z.enum([
  'costIncurredOn', // Date
  'amount', // Amount
  'person', // Person
  'project', // Project
  'category', // Category
]);

export type CostSortProperty = z.infer<typeof costSortPropertySchema>;

/**
 * Schema for validating the API response when fetching costs.
 * Wraps the cost listing data in a standard response structure.
 */
const costsResponseSchema = z.object({
  data: costListingSchema,
  metadata: responseMetadataSchema,
});

export interface CostFilter {
  pageSize: number;
  pageNumber?: number;
  sort?: Sort<CostSortProperty>;
  search?: string;
  startDate?: string;
  endDate?: string;
  personIds?: string[];
  projectIds?: string[];
  categories?: string[];
}

/**
 * Serializes a cost filter object into a query string for API requests.
 * This allows for a stable query key for the filter parameters.
 **/
export function serializeCostFilter(filter?: CostFilter): string {
  if (!filter) {
    return '';
  }

  const params = new URLSearchParams();

  params.append('limit', filter.pageSize.toString());
  if (filter.pageNumber) {
    params.append('page', filter.pageNumber.toString());
  }
  if (filter.sort) {
    params.append('sortBy', filter.sort.property);
    params.append('sortOrder', filter.sort.direction);
  }
  if (filter.search) {
    params.append('search', filter.search);
  }
  if (filter.startDate) {
    params.append('startDate', filter.startDate);
  }
  if (filter.endDate) {
    params.append('endDate', filter.endDate);
  }
  if (filter.personIds && filter.personIds.length > 0) {
    filter.personIds.forEach((id) => {
      params.append('personId', id);
    });
  }
  if (filter.projectIds && filter.projectIds.length > 0) {
    filter.projectIds.forEach((id) => {
      params.append('projectId', id);
    });
  }
  if (filter.categories && filter.categories.length > 0) {
    filter.categories.forEach((category) => {
      params.append('category', category);
    });
  }

  return params.toString();
}

/**
 * Fetches a list of costs from the API
 *
 * @param filter Optional filter parameters including sort and pagination
 * @returns A promise that resolves to cost results and total count
 * @throws Will throw if the API request fails or response validation fails
 */
export async function getCosts(filter: CostFilter = { pageSize: 50 }) {
  const serializedFilter = serializeCostFilter(filter);
  const url = serializedFilter ? `/v1/cost?${serializedFilter}` : '/v1/cost';
  const response = await api.get(url);

  const result = await parseResponse(costsResponseSchema, response.data, {
    schemaName: 'Costs',
    endpoint: url,
  });

  return result.data;
}
